import { useEffect, useState } from 'react'
import { api } from '../../api/client'
import type { Attachment, Delivery, RejectionReason } from '../../api/types'
import { useStores } from '../../app/StoreContext'
import {
  Badge,
  Button,
  Card,
  Empty,
  ErrorBox,
  Spinner,
  formatDate,
  formatDateTime,
} from '../../ui/primitives'
import { useAsync } from '../planning/useAsync'
import {
  deliveryTitle,
  documentLabel,
  documentTone,
  formatBytes,
  originLabel,
  statusLabel,
  statusTone,
} from '../planning/labels'

// Controllo dei documenti di carico: la coda di lavoro del back-office.
//
// Si prende in carico un gruppo di documenti, li si guarda uno dopo l'altro e per ciascuno si
// decide. La presa in carico scade da sola: nell'applicazione attuale un documento aperto e
// abbandonato restava bloccato finché qualcuno non se ne accorgeva, qui torna in coda da solo.

interface AssignNext {
  deliveryIds: string[]
  expiresAt: string
}

type Azione = 'approve' | 'reject' | 'suspend'

export function DocumentReviewPage() {
  const { storeName } = useStores()

  const [queue, setQueue] = useState<string[]>([])
  const [expiresAt, setExpiresAt] = useState<string>()
  const [index, setIndex] = useState(0)
  const [assigned, setAssigned] = useState(false)

  const [assigning, setAssigning] = useState(false)
  const [queueError, setQueueError] = useState<unknown>()

  const [reasonId, setReasonId] = useState('')
  const [rejectNote, setRejectNote] = useState('')
  const [suspendNote, setSuspendNote] = useState('')
  const [running, setRunning] = useState<Azione | undefined>()
  const [actionError, setActionError] = useState<unknown>()
  const [lastDone, setLastDone] = useState<string>()

  const reasons = useAsync<RejectionReason[]>(
    () => api.get<RejectionReason[]>('/config/rejection-reasons', { activeOnly: true }),
    [],
  )

  // La presa in carico vive sul server e sopravvive alla pagina: all'apertura si riprende
  // ciò che era rimasto in carico, altrimenti un documento preso e non deciso sparirebbe
  // dalla vista pur restando bloccato a nome dell'operatore.
  useEffect(() => {
    let cancelled = false
    api
      .get<string[]>('/document-review/assigned')
      .then((ids) => {
        if (cancelled || ids.length === 0) return
        setQueue(ids)
        setIndex(0)
        setAssigned(true)
        setLastDone('Ripresi i documenti che avevi già in carico.')
      })
      .catch(() => {
        /* niente da riprendere o errore transitorio: si parte comunque dalla coda vuota */
      })
    return () => {
      cancelled = true
    }
  }, [])

  const currentId = queue[index]
  const path = currentId ? `/deliveries/${encodeURIComponent(currentId)}` : ''

  const delivery = useAsync<Delivery | undefined>(
    () => (path ? api.get<Delivery>(path) : Promise.resolve(undefined)),
    [path],
  )
  const attachments = useAsync<Attachment[]>(
    () => (path ? api.get<Attachment[]>(`${path}/attachments`) : Promise.resolve([])),
    [path],
  )

  // --- presa in carico e rilascio ---------------------------------------------------------

  const prendiInCarico = () => {
    setAssigning(true)
    setQueueError(undefined)
    setActionError(undefined)
    api
      .post<AssignNext>('/document-review/assign-next')
      .then((res) => {
        setQueue(res.deliveryIds)
        setExpiresAt(res.expiresAt)
        setIndex(0)
        setAssigned(true)
        setLastDone(undefined)
      })
      .catch((e: unknown) => setQueueError(e))
      .finally(() => setAssigning(false))
  }

  const rilasciaTutto = () => {
    setAssigning(true)
    setQueueError(undefined)
    api
      .post<void>('/document-review/release-all')
      .then(() => {
        setQueue([])
        setExpiresAt(undefined)
        setIndex(0)
        setAssigned(false)
        setLastDone('Documenti rilasciati: tornano disponibili per gli altri operatori.')
      })
      .catch((e: unknown) => setQueueError(e))
      .finally(() => setAssigning(false))
  }

  /** Toglie dalla coda il documento appena trattato e passa al successivo. */
  const passaAlSuccessivo = (esito: string) => {
    setQueue((current) => current.filter((q) => q !== currentId))
    setReasonId('')
    setRejectNote('')
    setSuspendNote('')
    setLastDone(esito)
    // L'indice resta dov'è: togliendo l'elemento corrente, la stessa posizione contiene già
    // il documento successivo. Se era l'ultimo, l'indice finisce oltre la coda e si mostra
    // il messaggio di lavoro concluso.
  }

  const esegui = (azione: Azione, chiamata: Promise<void>, esito: string) => {
    setRunning(azione)
    setActionError(undefined)
    chiamata
      .then(() => passaAlSuccessivo(esito))
      .catch((e: unknown) => setActionError(e))
      .finally(() => setRunning(undefined))
  }

  const reasonList = reasons.data ?? []
  const rejectPossibile = reasonList.length > 0 && reasonId !== ''
  const suspendPossibile = suspendNote.trim().length >= 3
  const occupato = running !== undefined

  const d = delivery.data
  const allegato = attachments.data?.[0]
  const allegatoUrl = allegato ? api.url(`${path}/attachments/${allegato.id}`) : undefined

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Controllo documenti di carico</h1>
          <div className="muted">
            Coda di lavoro del back-office: si prende in carico un gruppo di documenti e si decide
            uno per uno.
          </div>
        </div>

        <div className="page-tools">
          <Button variant="primary" disabled={assigning} onClick={prendiInCarico}>
            {assigning ? 'Attendere…' : 'Prendi in carico'}
          </Button>
          <Button disabled={assigning || queue.length === 0} onClick={rilasciaTutto}>
            Rilascia tutto
          </Button>
        </div>
      </div>

      {expiresAt && queue.length > 0 && (
        <p className="muted">
          Presa in carico valida fino alle {formatDateTime(expiresAt)}: se il controllo si
          interrompe, i documenti tornano in coda da soli e nessun altro resta bloccato.
        </p>
      )}

      {queueError !== undefined && <ErrorBox error={queueError} onRetry={prendiInCarico} />}

      {lastDone && (
        <div className="notice notice-ok">
          <strong>{lastDone}</strong>
        </div>
      )}

      {/* --- coda vuota ------------------------------------------------------------------ */}
      {queue.length === 0 && !assigning && (
        <Empty>
          {assigned ?
            <>
              Non ci sono documenti da controllare: la coda è stata svuotata, oppure nessuna
              consegna ha un documento allegato in attesa di verifica.
              <div className="muted">
                I documenti entrano in questa coda quando un negozio allega il documento di carico
                dalla scheda della consegna.
              </div>
            </>
          : <>
              Nessun documento in carico.
              <div className="muted">
                Premi «Prendi in carico» per farti assegnare il prossimo gruppo di documenti da
                verificare.
              </div>
            </>
          }
        </Empty>
      )}

      {/* --- coda esaurita dopo aver lavorato -------------------------------------------- */}
      {queue.length > 0 && index >= queue.length && (
        <Empty>
          Hai esaurito i documenti presi in carico.
          <div className="muted">Premi «Prendi in carico» per ricevere il gruppo successivo.</div>
        </Empty>
      )}

      {/* --- documento corrente ---------------------------------------------------------- */}
      {currentId && (
        <>
          <Card
            title={`Documento ${index + 1} di ${queue.length}`}
            actions={
              <Button
                disabled={index + 1 >= queue.length}
                onClick={() => setIndex((i) => i + 1)}
                title="Salta questo documento e passa al prossimo, senza deciderlo"
              >
                Salta →
              </Button>
            }
          >
            <div className="progress" aria-hidden>
              <div className="progress-fill" style={{ width: `${((index + 1) / queue.length) * 100}%` }} />
            </div>

            {delivery.loading && <Spinner label="Carico la consegna…" />}
            {delivery.error !== undefined && <ErrorBox error={delivery.error} onRetry={delivery.reload} />}

            {d && (
              <div className="grid grid-4">
                <div className="field">
                  <span className="field-label">Punto vendita</span>
                  <span>
                    {d.storeCode} — {storeName(d.storeCode)}
                  </span>
                </div>
                <div className="field">
                  <span className="field-label">Fornitore</span>
                  <span>
                    {deliveryTitle(d)} <span className="muted">({d.supplierCode})</span>
                  </span>
                </div>
                <div className="field">
                  <span className="field-label">Ordine</span>
                  <span>
                    {d.orderNumber ?? '—'} <span className="muted">{originLabel(d)}</span>
                  </span>
                </div>
                <div className="field">
                  <span className="field-label">Consegna prevista</span>
                  <span>{formatDate(d.effectiveDate)}</span>
                </div>
                <div className="field">
                  <span className="field-label">Documento</span>
                  <span>
                    {d.documentNumber ?? '—'}
                    {d.documentDate && <span className="muted"> del {formatDate(d.documentDate)}</span>}
                  </span>
                </div>
                <div className="field">
                  <span className="field-label">Stato</span>
                  <span className="inline">
                    <Badge tone={statusTone(d.status)}>{statusLabel(d.status)}</Badge>
                    <Badge tone={documentTone(d.documentStatus)}>{documentLabel(d.documentStatus)}</Badge>
                  </span>
                </div>
              </div>
            )}
          </Card>

          <Card title="Anteprima dell'allegato">
            {attachments.loading && <Spinner label="Cerco l'allegato…" />}
            {attachments.error !== undefined && (
              <ErrorBox error={attachments.error} onRetry={attachments.reload} />
            )}

            {attachments.data && attachments.data.length === 0 && !attachments.loading && (
              <Empty>
                Questa consegna non ha allegati: non c'è nulla da verificare, conviene sospenderla
                spiegando che il documento non è arrivato.
              </Empty>
            )}

            {allegato && allegatoUrl && (
              <>
                <p className="muted">
                  {allegato.fileName} · {formatBytes(allegato.byteSize)} · caricato da{' '}
                  {allegato.uploadedBy} il {formatDateTime(allegato.uploadedAt)} ·{' '}
                  <a href={allegatoUrl} target="_blank" rel="noreferrer">
                    apri in una nuova scheda
                  </a>
                </p>

                {allegato.contentType === 'application/pdf' ?
                  <iframe className="doc-frame" src={allegatoUrl} title={allegato.fileName} />
                : allegato.contentType.startsWith('image/') ?
                  <img className="doc-image" src={allegatoUrl} alt={allegato.fileName} />
                : <div className="notice notice-info">
                    <strong>Anteprima non disponibile per «{allegato.contentType}».</strong>
                    <p>Apri il file in una nuova scheda per leggerlo.</p>
                  </div>
                }

                {attachments.data && attachments.data.length > 1 && (
                  <p className="muted">
                    Altri allegati su questa consegna:{' '}
                    {attachments.data.slice(1).map((a) => (
                      <span key={a.id}>
                        <a href={api.url(`${path}/attachments/${a.id}`)} target="_blank" rel="noreferrer">
                          {a.fileName}
                        </a>{' '}
                      </span>
                    ))}
                  </p>
                )}
              </>
            )}
          </Card>

          <Card title="Decisione">
            {actionError !== undefined && <ErrorBox error={actionError} />}

            <div className="grid grid-3">
              {/* approvazione */}
              <div>
                <h3 className="field-label">Approva</h3>
                <p className="muted">
                  Il documento corrisponde all'ordine: il carico può proseguire.
                </p>
                <Button
                  variant="primary"
                  disabled={occupato}
                  onClick={() =>
                    esegui(
                      'approve',
                      api.post<void>(`/document-review/${encodeURIComponent(currentId)}/approve`),
                      'Documento approvato.',
                    )
                  }
                >
                  {running === 'approve' ? 'Attendere…' : 'Approva'}
                </Button>
              </div>

              {/* rifiuto */}
              <div>
                <h3 className="field-label">Respingi</h3>

                {reasons.loading && <Spinner label="Carico le motivazioni…" />}
                {reasons.error !== undefined && <ErrorBox error={reasons.error} onRetry={reasons.reload} />}

                {!reasons.loading && reasons.error === undefined && reasonList.length === 0 && (
                  <div className="notice notice-warn">
                    <strong>Nessuna motivazione di rifiuto configurata.</strong>
                    <p>
                      Finché l'elenco è vuoto non è possibile respingere un documento: le
                      motivazioni si inseriscono in «Configurazioni», così tutti respingono con le
                      stesse parole e i motivi diventano confrontabili.
                    </p>
                  </div>
                )}

                {reasonList.length > 0 && (
                  <>
                    <label className="field">
                      <span className="field-label">Motivazione</span>
                      <select value={reasonId} onChange={(e) => setReasonId(e.target.value)}>
                        <option value="">— scegli —</option>
                        {reasonList.map((r) => (
                          <option key={r.id} value={String(r.id)}>
                            {r.description}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="field">
                      <span className="field-label">Nota (facoltativa)</span>
                      <textarea
                        rows={2}
                        value={rejectNote}
                        placeholder="Cosa deve correggere il negozio"
                        onChange={(e) => setRejectNote(e.target.value)}
                      />
                    </label>
                  </>
                )}

                <Button
                  variant="danger"
                  disabled={occupato || !rejectPossibile}
                  title={rejectPossibile ? undefined : 'Scegli prima una motivazione.'}
                  onClick={() =>
                    esegui(
                      'reject',
                      api.post<void>(`/document-review/${encodeURIComponent(currentId)}/reject`, {
                        reasonId: Number(reasonId),
                        note: rejectNote.trim() || undefined,
                      }),
                      'Documento respinto: il negozio lo vedrà sulla consegna.',
                    )
                  }
                >
                  {running === 'reject' ? 'Attendere…' : 'Respingi'}
                </Button>
              </div>

              {/* sospensione */}
              <div>
                <h3 className="field-label">Sospendi</h3>
                <p className="muted">
                  Serve quando manca un'informazione per decidere: la consegna resta in attesa con
                  scritto cosa si sta aspettando.
                </p>
                <label className="field">
                  <span className="field-label">Nota (obbligatoria)</span>
                  <textarea
                    rows={2}
                    value={suspendNote}
                    placeholder="Es. in attesa del documento leggibile dal fornitore"
                    onChange={(e) => setSuspendNote(e.target.value)}
                  />
                  <span className="field-hint">Almeno tre caratteri.</span>
                </label>
                <Button
                  disabled={occupato || !suspendPossibile}
                  title={suspendPossibile ? undefined : 'Scrivi prima la nota.'}
                  onClick={() =>
                    esegui(
                      'suspend',
                      api.post<void>(`/document-review/${encodeURIComponent(currentId)}/suspend`, {
                        note: suspendNote.trim(),
                      }),
                      'Documento sospeso.',
                    )
                  }
                >
                  {running === 'suspend' ? 'Attendere…' : 'Sospendi'}
                </Button>
              </div>
            </div>
          </Card>
        </>
      )}
    </>
  )
}
